import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, ExternalLink, Loader2, Unlink, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Stripe Connect onboarding card on the Vendor Dashboard. Today the underlying endpoints
 * are SCAFFOLDED — `POST /vendors/me/stripe/connect-start` returns an `about:blank#…`
 * placeholder URL until `STRIPE_CONNECT_CLIENT_ID` is set in `backend/.env`. The UI
 * acknowledges this state with a clear amber "Pending platform configuration" banner so
 * vendors aren't shown a broken button.
 *
 * Once the env var is set + an OAuth callback handler is added, this exact same UI flow
 * will work end-to-end with no frontend changes — that's the whole point of "Standard,
 * scaffolded".
 */
export default function StripeConnectCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/vendors/me/stripe/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startConnect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/vendors/me/stripe/connect-start", { return_url: window.location.origin + "/dashboard/vendor" });
      if (data.configured) {
        // Real OAuth — redirect to Stripe.
        window.location.href = data.url;
      } else {
        toast.error("Stripe Connect isn't fully configured by the platform yet. Once the admin sets STRIPE_CONNECT_CLIENT_ID, this button will redirect you to Stripe.", { duration: 6500 });
      }
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect this Stripe account from RentalCar.al?")) return;
    setBusy(true);
    try {
      await api.post("/vendors/me/stripe/disconnect");
      toast.success("Stripe account disconnected");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (!status) return null;
  const connected = !!status.stripe_account_id;

  return (
    <div className="rounded-3xl border border-border bg-white p-6" data-testid="stripe-connect-card">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-2xl bg-violet-50 flex items-center justify-center text-violet-700">
          <CreditCard className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-lg font-medium">Stripe Connect (Standard)</h3>
            {connected
              ? <Badge variant="default" className="bg-emerald-600">Connected</Badge>
              : <Badge variant="secondary">Not connected</Badge>}
          </div>
          <p className="text-sm text-neutral-600 mt-1">
            Connect your own Stripe account to receive payouts directly. The platform's commission ({status.commission_percent ?? "12"}% by default) is taken automatically on every charge.
          </p>

          {connected ? (
            <div className="mt-4 space-y-2">
              <div className="text-xs text-neutral-500">Account: <code className="text-neutral-900">{status.stripe_account_id}</code></div>
              <div className="text-xs">
                Payouts: {status.stripe_payouts_enabled
                  ? <span className="text-emerald-700 font-semibold inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Enabled</span>
                  : <span className="text-amber-700 font-semibold">Pending Stripe verification</span>}
              </div>
              <Button variant="outline" size="sm" className="rounded-full mt-2" onClick={disconnect} disabled={busy} data-testid="stripe-disconnect">
                <Unlink className="h-3.5 w-3.5 mr-1.5" /> Disconnect
              </Button>
            </div>
          ) : (
            <Button onClick={startConnect} disabled={busy} className="mt-4 rounded-full bg-violet-600 hover:bg-violet-700" data-testid="stripe-connect-start">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-2" />}
              Connect with Stripe
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
